import React from 'react';
import { Truck, Clock, MapPin, Package, Shield, CreditCard, Phone, Calendar, CheckCircle, Crown } from 'lucide-react';
import { useSiteSettings } from '../context/SiteSettingsContext';

const ShippingPolicy = () => {
  const { settings } = useSiteSettings();
  
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Hero Section */}
      <div className="bg-[#0A0A0A] text-white py-20 border-b border-[#D4AF37]/20">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <div className="w-20 h-20 bg-[#D4AF37]/20 rounded-full flex items-center justify-center mx-auto mb-6 border border-[#D4AF37]/50">
            <Truck className="h-10 w-10 text-[#D4AF37]" />
          </div>
          <h1 className="text-5xl lg:text-6xl font-bold mb-4 font-heading">Shipping & Delivery</h1>
          <p className="text-xl text-[#888888] font-body">Premium delivery service for your luxury collectibles</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-16 max-w-4xl">
        {/* Free Shipping Banner */}
        <div className="bg-gradient-to-r from-[#D4AF37] via-[#F4E4BC] to-[#D4AF37] text-[#0A0A0A] p-8 rounded-2xl mb-12 shadow-xl shadow-[#D4AF37]/20">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Crown className="h-6 w-6" />
                <span className="font-bold uppercase tracking-wide">Complimentary</span>
              </div>
              <h2 className="text-3xl font-bold font-heading mb-2">White-Glove Shipping</h2>
              <p className="text-[#0A0A0A]/80 font-body text-lg">On all orders over $100 within the USA</p>
            </div>
            <div className="text-center md:text-right">
              <div className="text-5xl font-bold font-heading">$100+</div>
              <div className="text-[#0A0A0A]/70 font-body">For free shipping</div>
            </div>
          </div>
        </div>

        {/* Shipping Methods */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 font-heading flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center border border-[#D4AF37]/30">
              <Truck className="h-6 w-6 text-[#D4AF37]" />
            </div>
            Delivery Options
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-[#141414] p-8 rounded-2xl border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-colors">
              <div className="text-[#D4AF37] font-bold text-sm uppercase tracking-wide mb-2">Standard</div>
              <h3 className="font-bold text-white text-xl mb-3 font-heading">Express Delivery</h3>
              <div className="text-4xl font-bold text-[#D4AF37] mb-2">$9.99</div>
              <p className="text-[#888888] font-body mb-4">or FREE on orders $100+</p>
              <div className="flex items-center gap-2 text-[#888888] font-body">
                <Clock className="h-4 w-4 text-[#D4AF37]" />
                <span>5-7 business days</span>
              </div>
            </div>

            <div className="bg-[#141414] p-8 rounded-2xl border-2 border-[#D4AF37] shadow-lg shadow-[#D4AF37]/20 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-[#0A0A0A] px-4 py-1 rounded-full text-sm font-bold">
                RECOMMENDED
              </div>
              <div className="text-[#D4AF37] font-bold text-sm uppercase tracking-wide mb-2">Priority</div>
              <h3 className="font-bold text-white text-xl mb-3 font-heading">Premium Shipping</h3>
              <div className="text-4xl font-bold text-[#D4AF37] mb-2">$19.99</div>
              <p className="text-[#888888] font-body mb-4">Priority handling included</p>
              <div className="flex items-center gap-2 text-[#888888] font-body">
                <Clock className="h-4 w-4 text-[#D4AF37]" />
                <span>2-3 business days</span>
              </div>
            </div>

            <div className="bg-[#141414] p-8 rounded-2xl border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-colors">
              <div className="text-[#D4AF37] font-bold text-sm uppercase tracking-wide mb-2">VIP</div>
              <h3 className="font-bold text-white text-xl mb-3 font-heading">Next-Day Delivery</h3>
              <div className="text-4xl font-bold text-[#D4AF37] mb-2">$39.99</div>
              <p className="text-[#888888] font-body mb-4">White-glove service</p>
              <div className="flex items-center gap-2 text-[#888888] font-body">
                <Clock className="h-4 w-4 text-[#D4AF37]" />
                <span>Next business day</span>
              </div>
            </div>
          </div>
        </section>

        {/* Processing Time */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 font-heading flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center border border-[#D4AF37]/30">
              <Calendar className="h-6 w-6 text-[#D4AF37]" />
            </div>
            Order Processing
          </h2>

          <div className="bg-[#141414] rounded-2xl border border-[#D4AF37]/20 overflow-hidden">
            <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-[#D4AF37]/20">
              <div className="p-8">
                <h3 className="font-bold text-white text-xl mb-4 font-heading">Processing Timeline</h3>
                <ul className="space-y-3 text-[#888888] font-body">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span>Orders before <strong className="text-white">2:00 PM EST</strong> ship same day</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span>Weekend orders processed next business day</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span>Peak season may add 1-2 days processing</span>
                  </li>
                </ul>
              </div>
              <div className="p-8">
                <h3 className="font-bold text-white text-xl mb-4 font-heading">Holiday Schedule</h3>
                <p className="text-[#888888] font-body mb-4">We observe the following holidays:</p>
                <ul className="space-y-2 text-[#888888] font-body text-sm">
                  <li>• New Year's Day & Memorial Day</li>
                  <li>• Independence Day & Labor Day</li>
                  <li>• Thanksgiving & Christmas</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Delivery Areas */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 font-heading flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center border border-[#D4AF37]/30">
              <MapPin className="h-6 w-6 text-[#D4AF37]" />
            </div>
            Delivery Regions
          </h2>

          <div className="bg-[#141414] rounded-2xl border border-[#D4AF37]/20 p-8">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="font-bold text-white text-xl mb-4 font-heading">Continental USA</h3>
                <p className="text-[#888888] font-body mb-4">
                  Full delivery service to all 48 contiguous states with standard timeframes.
                </p>
                <ul className="space-y-2 text-[#888888] font-body">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-[#D4AF37]" />
                    Metropolitan areas: Standard delivery
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-[#D4AF37]" />
                    Remote areas: +1-2 business days
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-white text-xl mb-4 font-heading">Alaska & Hawaii</h3>
                <p className="text-[#888888] font-body mb-4">
                  Extended delivery available with adjusted pricing:
                </p>
                <ul className="space-y-2 text-[#888888] font-body">
                  <li className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-[#D4AF37]" />
                    Standard: 7-14 days (+$15)
                  </li>
                  <li className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-[#D4AF37]" />
                    Priority: 5-7 days (+$25)
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Package Protection */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 font-heading flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center border border-[#D4AF37]/30">
              <Shield className="h-6 w-6 text-[#D4AF37]" />
            </div>
            Premium Protection
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-[#141414] rounded-2xl border border-[#D4AF37]/20 p-8">
              <h3 className="font-bold text-white text-xl mb-4 font-heading">Luxury Packaging</h3>
              <p className="text-[#888888] font-body mb-4">
                Every collectible is meticulously packaged to ensure pristine arrival.
              </p>
              <ul className="space-y-2 text-[#888888] font-body">
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-[#D4AF37]" />
                  Premium protective materials
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-[#D4AF37]" />
                  Double-boxed for fragile items
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-[#D4AF37]" />
                  Elegant branded presentation
                </li>
              </ul>
            </div>
            <div className="bg-[#141414] rounded-2xl border border-[#D4AF37]/20 p-8">
              <h3 className="font-bold text-white text-xl mb-4 font-heading">Delivery Guarantee</h3>
              <p className="text-[#888888] font-body mb-4">
                Your satisfaction is guaranteed with our comprehensive protection.
              </p>
              <ul className="space-y-2 text-[#888888] font-body">
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-[#D4AF37]" />
                  Full insurance on all shipments
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-[#D4AF37]" />
                  Immediate replacement if damaged
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-[#D4AF37]" />
                  Signature required available
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="bg-gradient-to-r from-[#D4AF37] via-[#F4E4BC] to-[#D4AF37] text-[#0A0A0A] p-8 rounded-2xl shadow-xl shadow-[#D4AF37]/20">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="text-2xl font-bold font-heading mb-2">Need Assistance?</h2>
              <p className="text-[#0A0A0A]/80 font-body">Our concierge team is ready to help</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href={`mailto:${settings?.email || 'support@boxverra.com'}`} className="flex items-center gap-2 bg-[#0A0A0A] text-[#D4AF37] px-6 py-3 rounded-full font-body transition-colors hover:bg-[#1A1A1A]">
                <CreditCard className="h-5 w-5" />
                {settings?.email || 'support@boxverra.com'}
              </a>
              <a href={`tel:${settings?.phone?.replace(/\D/g, '') || '18001234567'}`} className="flex items-center gap-2 bg-[#0A0A0A] text-[#D4AF37] px-6 py-3 rounded-full font-body transition-colors hover:bg-[#1A1A1A]">
                <Phone className="h-5 w-5" />
                {settings?.phone || '+1 (800) 123-4567'}
              </a>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ShippingPolicy;
